/**
 * Período de cada mês, por fonte (o "fechamento" do mês).
 *
 * Regra: o mês vai do dia 1º ao último dia (na prática, até o último dia útil).
 * Exceção combinada com a gerente fica em FECHAMENTOS_ESPECIAIS (dominio/regras.ts),
 * e o mês seguinte a uma exceção começa no dia depois do fim dela.
 * Ex.: Bauny 09/2026 = 01/09 a 01/10 (as notas de 01/10 contaram em setembro),
 *      então Bauny 10/2026 = 02/10 a 31/10. No Winthor, os dois meses são do calendário.
 *
 * Lê: as exceções de dominio/regras.ts.
 * Usado por: dominio/painel.ts, importacao/ (prévia), dados/usar-dados-do-mes.ts e telas/.
 */
import { diaSeguinte, limitesDoMes, mesDaData, mesesDepois } from './datas.ts';
import { FECHAMENTOS_ESPECIAIS } from './regras.ts';
import type { DataIso, Fonte, Mes } from './tipos.ts';

export interface Periodo {
  inicio: DataIso;
  fim: DataIso;
}

/** Do primeiro ao último dia que contam no mês, nesta fonte. */
export function periodoDoMes(fonte: Fonte, mes: Mes): Periodo {
  const excecoes = FECHAMENTOS_ESPECIAIS[fonte] ?? {};
  const especial = excecoes[mes];
  if (especial) return especial;
  const calendario = limitesDoMes(mes);
  const anterior = excecoes[mesesDepois(mes, -1)];
  return { inicio: anterior ? diaSeguinte(anterior.fim) : calendario.inicio, fim: calendario.fim };
}

/** Em que mês uma venda conta, nesta fonte (ex.: Bauny, 01/10/2026 → setembro). */
export function mesDaVenda(fonte: Fonte, data: DataIso): Mes {
  const doCalendario = mesDaData(data);
  for (const mes of [doCalendario, mesesDepois(doCalendario, -1), mesesDepois(doCalendario, 1)]) {
    const { inicio, fim } = periodoDoMes(fonte, mes);
    if (data >= inicio && data <= fim) return mes;
  }
  return doCalendario;
}

/** true se o mês desta fonte não é o do calendário (para avisar na tela). */
export function fechamentoEspecial(fonte: Fonte, mes: Mes): boolean {
  const periodo = periodoDoMes(fonte, mes);
  const calendario = limitesDoMes(mes);
  return periodo.inicio !== calendario.inicio || periodo.fim !== calendario.fim;
}
